import {
  GetStaticPaths,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticProps,
} from 'next';
import React, { useEffect, useState } from 'react';
import { TypeAgeBasedPlayKitLandingPage } from '@/core/generated-types';
import {
  buildCanonicalUrl,
  defaultCacheRevalidation,
} from '@/core/contentfulConfig';
import {
  CONTENT_TYPE_PRODUCT_PAGE,
  YOTPO_PLAYKIT_PROGRAM_PRODUCT_IDS,
  getShopifyDomain,
} from '@/core/constants';
import { loadLayout } from '@/utils/loadLayout';
import getPathsFromEntries from '@/utils/pathBuilder';
import {
  CustomSchema,
  DynamicComponent,
  Errors,
  Seo,
  YotpoReviewsWidget,
} from '@/components';
import { getEntriesByPageType, ContentfulPreviewData } from '@/lib/contentful';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import fetchShopifyProducts from '@/lib/middleware/fetchShopifyProducts';
import '@/components/ageBasedPKLD/style.scss';
import { useYotpoScript } from '@/hooks/useYotpoScript';
import { useTrackPlayKitPLPProducts } from '@/hooks/useTrackPlayKitPLPProducts';
import { shouldUseYotpoReviews } from '@/utils/helpers';

export interface ProductPageProps {
  error: string;
  page: TypeAgeBasedPlayKitLandingPage;
}

const AgeBasedPlayKitLandingPage = ({ error, page }: ProductPageProps) => {
  useYotpoScript();
  useTrackPlayKitPLPProducts();
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const { errors, heading } = findApplicableErrorDetails([]);
  const p = page.fields;

  const shopifyProductID =
    YOTPO_PLAYKIT_PROGRAM_PRODUCT_IDS[getShopifyDomain()] || null;

  const canonicalUrl = buildCanonicalUrl(page);
  const shouldIndex = ![
    '/products/the-play-kits/baby',
    '/products/the-play-kits/1-year-old',
    '/products/the-play-kits/2-year-old',
    '/products/the-play-kits/3-year-old',
    '/products/the-play-kits/4-year-old',
  ].includes(canonicalUrl);

  if (error) {
    return <Errors errors={errors?.concat(error)} heading={heading} />;
  }

  return (
    <>
      <Seo
        title={p.pageMetafields?.fields.pageTitle}
        description={p.pageMetafields?.fields.pageDescription}
        nofollow={!shouldIndex}
        noindex={!shouldIndex}
        canonical={canonicalUrl}
      />
      {p.customSchema?.fields && <CustomSchema {...p.customSchema?.fields} />}
      {hydrated && (
        <>
          {page.fields?.components &&
            page.fields?.components.map((component, i) => (
              <DynamicComponent
                key={`${component.sys.id}-${i}`}
                component={component}
              />
            ))}
        </>
      )}
      {page.fields?.enableYotpoReviewsWidget &&
        shouldUseYotpoReviews() &&
        shopifyProductID && (
          <div className="mx-auto max-w-[1128px] mt-2 px-6 lg:px-0 lg:mt-12">
            <YotpoReviewsWidget productID={shopifyProductID} />
          </div>
        )}
      {page.fields?.emailCaptureComponent && (
        <DynamicComponent component={page.fields?.emailCaptureComponent} />
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths =
  async (): Promise<GetStaticPathsResult> => {
    const pageType = CONTENT_TYPE_PRODUCT_PAGE.AGE_BASED_PLAY_KITS_LANDING_PAGE;
    const getEntries = await getEntriesByPageType({ include: 1 }, { pageType });
    const paths = getPathsFromEntries(getEntries);

    return {
      paths,
      fallback: false,
    };
  };

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}: GetStaticPropsContext) => {
  const isUSStore = process.env.NEXT_PUBLIC_REGION === 'US';

  if (!isUSStore) {
    return {
      redirect: {
        destination: '/products/the-play-kits',
        permanent: false,
      },
    };
  }
  const locale = params?.locale as string;
  const slug = params?.slug as string;
  const pageTypes = CONTENT_TYPE_PRODUCT_PAGE;
  const previewDataPayload = previewData as ContentfulPreviewData;

  const [pagesByTypes, layout] = await Promise.all([
    getEntriesByPageType(
      {
        'fields.slug': slug,
        locale,
      },
      pageTypes,
      preview,
      previewDataPayload,
    ),
    loadLayout(locale, preview, previewDataPayload),
  ]);

  const { globalNav, header, footer, footerLegal } = layout;

  let page = null;

  // Finds the first page type that has an item in it.
  for (const pagesByType of pagesByTypes) {
    if (pagesByType.items.length > 0) {
      page = pagesByType;
      break;
    }
  }

  if (!page || page.items?.length === 0) {
    return {
      notFound: true,
    };
  }

  if (page) {
    page = await fetchShopifyProducts(page);
  }

  const contentfulPage = page.items?.[0];

  return {
    props: {
      page: contentfulPage || null,
      header,
      footer,
      footerLegal,
      globalNav,
    },
    revalidate: defaultCacheRevalidation,
  };
};

export default AgeBasedPlayKitLandingPage;
